import { useState } from "react";

import HoldersTable from "@/components/app/holders/holders-table";
import TokenPostLaunchAmm from "@/components/app/profile/token-post-launch-amm";
import { TokenPostLaunchOverview } from "@/components/app/profile/token-post-launch-overview";
import TokenPreLaunchAmm from "@/components/app/profile/token-pre-launch-amm";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import type { CoinPropertiesResponse } from "@/hooks/use-coin-properties-query";

import { CoinInfoWidget } from "../app-layout/coin-info-widget";
import { USER_PROFILE_TAB_ITEMS_SUBCATEGORIES } from "../feed/feed-user-wrapper";
import { TokenPreLaunchOverview } from "./token-pre-launch-overview";

interface TokenDetailsProps {
  tokenDetails: CoinPropertiesResponse;
  coinPublicKey: string;
  userTokenSubTab: USER_PROFILE_TAB_ITEMS_SUBCATEGORIES | null;
  syncURLWithActiveTab: (tab: string) => void;
}

const TokenDetails = ({
  tokenDetails,
  coinPublicKey,
  userTokenSubTab,
  syncURLWithActiveTab,
}: TokenDetailsProps) => {
  const [activeTab, setActiveTab] =
    useState<USER_PROFILE_TAB_ITEMS_SUBCATEGORIES>(
      userTokenSubTab ?? USER_PROFILE_TAB_ITEMS_SUBCATEGORIES.OVERVIEW,
    );
  const {
    loading: loadingExchangeRates,
    focusExchangeRate,
    appState,
  } = useFocusMarketData();
  const { coinProperties, ammRunning } = tokenDetails;

  if (loadingExchangeRates || !focusExchangeRate || !appState) {
    return <Skeleton className="h-[45px] w-full" />;
  }

  return (
    <section>
      <Tabs
        value={activeTab}
        className="m-4"
        onValueChange={(val) => {
          setActiveTab(val as USER_PROFILE_TAB_ITEMS_SUBCATEGORIES);
          syncURLWithActiveTab(val);
        }}
      >
        <div className="w-full overflow-x-auto">
          <TabsList
            className="
              m-auto mb-4 flex min-w-fit items-center justify-center gap-1
              rounded-full border border-transparent bg-card p-1 text-center
              text-muted-foreground
            "
          >
            <TabsTrigger
              value={USER_PROFILE_TAB_ITEMS_SUBCATEGORIES.OVERVIEW}
              className="
                flex gap-2 rounded-full text-xs
                data-active:bg-background
                data-active:text-muted-foreground
                sm:text-sm
              "
            >
              Overview
            </TabsTrigger>

            <TabsTrigger
              value={USER_PROFILE_TAB_ITEMS_SUBCATEGORIES.AMM}
              className="
                flex gap-2 rounded-full text-xs
                data-active:bg-background
                data-active:text-muted-foreground
                sm:text-sm
              "
            >
              AMM
            </TabsTrigger>

            <TabsTrigger
              value={USER_PROFILE_TAB_ITEMS_SUBCATEGORIES.HOLDERS}
              className="
                flex gap-2 rounded-full text-xs
                data-active:bg-background
                data-active:text-muted-foreground
                sm:text-sm
              "
            >
              Holders
            </TabsTrigger>

            <TabsTrigger
              value={USER_PROFILE_TAB_ITEMS_SUBCATEGORIES.PROPERTIES}
              className="
                flex gap-2 rounded-full text-xs
                data-active:bg-background
                data-active:text-muted-foreground
                sm:text-sm
                lg:hidden
              "
            >
              Properties
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={USER_PROFILE_TAB_ITEMS_SUBCATEGORIES.OVERVIEW}>
          {ammRunning ? (
            <TokenPostLaunchOverview
              {...{
                tokenDetails,
                publicKey: coinPublicKey,
                focusExchangeRate,
              }}
            />
          ) : (
            <TokenPreLaunchOverview
              {...{
                tokenDetails,
                publicKey: coinPublicKey,
                focusExchangeRate,
                setActiveTab,
              }}
            />
          )}
        </TabsContent>

        <TabsContent value={USER_PROFILE_TAB_ITEMS_SUBCATEGORIES.AMM}>
          {ammRunning ? (
            <TokenPostLaunchAmm
              tokenDetails={tokenDetails}
              coinPublicKey={coinPublicKey}
              focusExchangeRate={focusExchangeRate}
              desoExchangeRate={appState.USDCentsPerDeSoExchangeRate}
            />
          ) : (
            <TokenPreLaunchAmm
              tokenDetails={tokenDetails}
              coinPublicKey={coinPublicKey}
              focusExchangeRate={focusExchangeRate}
              desoExchangeRate={appState.USDCentsPerDeSoExchangeRate}
            />
          )}
        </TabsContent>

        <TabsContent value={USER_PROFILE_TAB_ITEMS_SUBCATEGORIES.HOLDERS}>
          <div className="overflow-hidden">
            <HoldersTable
              height={600}
              publicKey={coinProperties.Profile.PublicKeyBase58Check}
              className="rounded-2xl border border-border"
              showHoldersSearch={true}
            />
          </div>
        </TabsContent>

        <TabsContent value={USER_PROFILE_TAB_ITEMS_SUBCATEGORIES.PROPERTIES}>
          <div className="p-0">
            <CoinInfoWidget />
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
};

export default TokenDetails;
